import {
  configureEmergencyNetwork,
  getEmergencyNetworkCapabilities,
  getEmergencyNetworkCredentials,
  getEmergencyNetworkDiagnostics,
  getEmergencyNetworkProfile,
  getEmergencyNetworkStatus,
  restartEmergencyNetwork,
  startEmergencyNetwork,
  startEmergencyNetworkTest,
  stopEmergencyNetwork,
  stopEmergencyNetworkTest,
} from '@/services/localHub/emergencyNetworkService';

function discovery(rootState) {
  const value = rootState.device.localControlDiscovery;
  if (!value?.url || !value?.profile?.hub_uuid) {
    throw new Error('LOCAL_HUB_UNAVAILABLE');
  }
  return value;
}

function initialState() {
  return {
    status: {},
    capabilities: {},
    profile: {},
    credentials: null,
    diagnostics: null,
    loading: false,
    error: null,
    lastUpdatedAt: null,
  };
}

function storeFacade(context) {
  return {
    state: context.rootState,
    commit: (type, payload) => context.commit(type, payload, { root: true }),
    dispatch: (type, payload) => context.dispatch(type, payload, { root: true }),
  };
}

async function run({ commit, rootState }, operation) {
  commit('setLoading', true);
  commit('setError', null);
  try {
    const result = await operation(discovery(rootState));
    if (result?.network_state) commit('setStatus', result);
    if (result?.profile) commit('setProfile', result.profile);
    return result;
  } catch (error) {
    commit(
      'setError',
      error?.response?.data?.detail?.code
        || error?.response?.data?.detail
        || error?.response?.data?.error
        || error?.message
        || 'EMERGENCY_NETWORK_REQUEST_FAILED',
    );
    throw error;
  } finally {
    commit('setLoading', false);
  }
}

export default {
  namespaced: true,
  state: initialState,
  mutations: {
    setLoading(state, value) {
      state.loading = Boolean(value);
    },
    setError(state, value) {
      state.error = value || null;
    },
    setStatus(state, value) {
      state.status = { ...state.status, ...(value || {}) };
      if (value?.profile) state.profile = { ...state.profile, ...value.profile };
      state.lastUpdatedAt = new Date().toISOString();
    },
    setCapabilities(state, value) {
      state.capabilities = value || {};
    },
    setProfile(state, value) {
      state.profile = { ...state.profile, ...(value || {}) };
    },
    setCredentials(state, value) {
      state.credentials = value || null;
    },
    setDiagnostics(state, value) {
      state.diagnostics = value || null;
    },
    applyEvent(state, event) {
      state.status = {
        ...state.status,
        network_state: event.network_state || state.status.network_state,
      };
      state.profile = {
        ...state.profile,
        ssid: event.ssid || state.profile.ssid,
        hub_endpoint: event.hub_endpoint || state.profile.hub_endpoint,
        certificate_fingerprint: event.certificate_fingerprint
          || state.profile.certificate_fingerprint,
        operation_epoch: event.operation_epoch || state.profile.operation_epoch,
        session_uuid: event.session_uuid || state.profile.session_uuid,
      };
      state.lastUpdatedAt = new Date().toISOString();
    },
  },
  actions: {
    async load(context) {
      return run(context, async (target) => {
        const store = storeFacade(context);
        const [status, profile] = await Promise.all([
          getEmergencyNetworkStatus(store, target),
          getEmergencyNetworkProfile(store, target),
        ]);
        const capabilities = status.diagnostics_enabled
          ? await getEmergencyNetworkCapabilities(store, target)
          : {};
        context.commit('setStatus', status);
        context.commit('setCapabilities', capabilities);
        context.commit('setProfile', profile);
        return status;
      });
    },
    configure(context, configuration) {
      return run(context, async (target) => {
        const result = await configureEmergencyNetwork(
          storeFacade(context), target, configuration,
        );
        context.commit('setCredentials', result);
        await context.dispatch('load');
        return result;
      });
    },
    credentials(context) {
      return run(context, async (target) => {
        const result = await getEmergencyNetworkCredentials(
          storeFacade(context), target,
        );
        context.commit('setCredentials', result);
        return result;
      });
    },
    diagnostics(context) {
      return run(context, async (target) => {
        const result = await getEmergencyNetworkDiagnostics(
          storeFacade(context), target,
        );
        context.commit('setDiagnostics', result);
        return result;
      });
    },
    start(context) {
      return run(context, (target) => startEmergencyNetwork(
        storeFacade(context), target,
      ));
    },
    stop(context) {
      return run(context, (target) => stopEmergencyNetwork(
        storeFacade(context), target,
      ));
    },
    restart(context) {
      return run(context, (target) => restartEmergencyNetwork(
        storeFacade(context), target,
      ));
    },
    startTest(context) {
      return run(context, (target) => startEmergencyNetworkTest(
        storeFacade(context), target,
      ));
    },
    stopTest(context) {
      return run(context, (target) => stopEmergencyNetworkTest(
        storeFacade(context), target,
      ));
    },
  },
};
